import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  type User as FirebaseUser,
} from "firebase/auth";
import { auth } from "./config";
import { ApiError, apiClient } from "../api/base.service";
import type { User, Startup, CreateUserData } from "@/types/startup.types";

export interface SignUpData {
  email: string;
  password: string;
  displayName: string;
  startupName?: string;
  initialFunding?: number;
}

export interface SignInData {
  email: string;
  password: string;
}

export function isProfileNotFoundError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 404;
  }
  return error instanceof Error && error.message.includes("status: 404");
}

class AuthService {
  private googleProvider: GoogleAuthProvider;

  constructor() {
    this.googleProvider = new GoogleAuthProvider();
    this.googleProvider.addScope("email");
    this.googleProvider.addScope("profile");
  }

  // Get current Firebase user
  getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }

  // Get auth token for API calls
  async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) return null;
    return user.getIdToken();
  }

  // Sign in with Google
  async signInWithGoogle(): Promise<{
    user: FirebaseUser;
    isNewUser: boolean;
    profile: User | null;
  }> {
    try {
      const result = await signInWithPopup(auth, this.googleProvider);
      const user = result.user;

      console.log("Google sign in for user:", user.email, user.uid);

      let isNewUser = false;
      let profile: User | null = null;
      const token = await user.getIdToken();

      try {
        profile = await apiClient.get<User>("/auth/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Found existing profile:", profile);
      } catch (error) {
        console.log("Profile check error:", error);
        if (isProfileNotFoundError(error)) {
          console.log("No profile found - this is a new user");
          isNewUser = true;
        } else {
          console.log("Unexpected error checking profile:", error);
        }
      }

      console.log("isNewUser:", isNewUser);
      return { user, isNewUser, profile };
    } catch (error) {
      console.error("Google sign in error:", error);
      throw error;
    }
  }

  // Create startup for existing user (onboarding or additional startup)
  async createStartup(startupData: {
    startupName: string;
    initialFunding?: number;
    industry?: string;
    description?: string;
    currency?: string;
    foundedDate?: Date;
    startDate?: Date;
    endDate?: Date;
  }): Promise<{ startup: Startup }> {
    try {
      const token = await this.getAuthToken();
      if (!token) throw new Error("Not authenticated");

      const user = this.getCurrentUser();
      if (!user) throw new Error("No user found");

      const headers = { Authorization: `Bearer ${token}` };

      // Ensure user profile exists before creating a startup
      try {
        await apiClient.get<User>("/auth/profile", { headers });
      } catch (error) {
        if (error instanceof Error && error.message.includes("status: 404")) {
          await apiClient.post<User>(
            "/auth/profile",
            {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || "",
            },
            { headers },
          );
        } else {
          throw error;
        }
      }

      const now = new Date();
      const foundedDate = startupData.foundedDate || startupData.startDate || now;

      const startup = await apiClient.post<Startup>(
        "/startups",
        {
          name: startupData.startupName,
          initialFunding: startupData.initialFunding ?? 0,
          industry: startupData.industry,
          description: startupData.description,
          currency: startupData.currency || "USD",
          foundedDate: foundedDate.toISOString(),
          startDate: (startupData.startDate || foundedDate).toISOString(),
          endDate: startupData.endDate?.toISOString(),
          displayName: user.displayName || "Owner",
        },
        { headers },
      );

      return { startup };
    } catch (error) {
      console.error("Create startup error:", error);
      throw error;
    }
  }

  // Sign up with Google (same as sign in, but with optional startup data)
  async signUpWithGoogle(startupData?: {
    startupName?: string;
    initialFunding?: number;
  }): Promise<{ user: FirebaseUser; profile: User }> {
    try {
      const result = await signInWithPopup(auth, this.googleProvider);
      const user = result.user;

      // Create user profile via API
      const token = await user.getIdToken();
      const userProfile = await apiClient.post<User>(
        "/auth/profile",
        {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || "",
          startupName: startupData?.startupName,
          initialFunding: startupData?.initialFunding,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return { user, profile: userProfile };
    } catch (error) {
      console.error("Google sign up error:", error);
      throw error;
    }
  }

  // Sign up new user
  async signUp(
    data: SignUpData,
  ): Promise<{ user: FirebaseUser; profile: User }> {
    try {
      // Create Firebase Auth user
      const credential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password,
      );

      // Update Firebase Auth profile
      await updateProfile(credential.user, {
        displayName: data.displayName,
      });

      // Create user profile via API
      const token = await credential.user.getIdToken();
      const userProfile = await apiClient.post<User>(
        "/auth/profile",
        {
          uid: credential.user.uid,
          email: credential.user.email,
          displayName: data.displayName,
          startupName: data.startupName,
          initialFunding: data.initialFunding,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return { user: credential.user, profile: userProfile };
    } catch (error) {
      console.error("Sign up error:", error);
      throw error;
    }
  }

  // Sign in existing user
  async signIn(data: SignInData): Promise<FirebaseUser> {
    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        data.email,
        data.password,
      );
      return credential.user;
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  }

  // Sign out user
  async signOut(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  }

  // Reset password
  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Password reset error:", error);
      throw error;
    }
  }

  // Get user profile from API
  async getUserProfile(): Promise<User | null> {
    try {
      const token = await this.getAuthToken();
      if (!token) return null;

      return apiClient.get<User>("/auth/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error("Get profile error:", error);

      // Check if it's a CORS error and provide more helpful logging
      if (
        error instanceof TypeError &&
        error.message.includes("Failed to fetch")
      ) {
        console.warn(
          "CORS or network error when fetching profile. This is expected in development if the API server is not running.",
        );
      }

      return null;
    }
  }

  // Update user profile
  async updateUserProfile(data: Partial<CreateUserData>): Promise<User> {
    try {
      const token = await this.getAuthToken();
      if (!token) throw new Error("Not authenticated");

      return apiClient.put<User>("/auth/profile", data, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error("Update profile error:", error);
      throw error;
    }
  }
}

export const authService = new AuthService();
